/**
 * `/alerts/history` — bounded historical search.
 *
 * Answers "what happened during the selected period?", so both time boundaries
 * are always sent and they come from the SHARED application range picker in the
 * workspace header — History deliberately has no date control of its own, which
 * is what keeps it from becoming a second, competing source of "current work".
 *
 * Offset/page pagination is correct here precisely because the result set is a
 * stable bounded search, unlike the mutating Queue.
 */
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { useGetAllLocations } from "@/locations/api/queries/location.queries.ts";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import { useAlertHistory } from "@/alerts/api/queries/alerts-workspace.queries";
import {
  ALERT_PAGE_SIZES,
  useAlertsWorkspaceStore,
} from "@/alerts/stores/use-alerts-workspace-store";
import {
  ALERT_TYPES,
  AlertStatus,
  alertStatusLabel,
  alertTypeLabel,
} from "@/alerts/types/machine-alert";
import { AlertMasterDetail } from "@/alerts/components/workspace/alert-master-detail";
import { AlertDetailPane } from "@/alerts/components/workspace/alert-detail-pane";
import { AlertsScopeGate } from "@/alerts/components/workspace/alerts-scope-gate";
import {
  ConfidenceRange,
  MultiSelect,
  OrderingToggle,
  PageSizeSelect,
  ScopeSelect,
} from "@/alerts/components/workspace/alert-filter-controls";
import { useGetClassificationTaxonomy } from "@/ml-exports/api/queries/taxonomy.queries";
import {
  HistoryEmptyState,
  RefreshButton,
  RefreshingChip,
  TruncatedNotice,
} from "@/alerts/components/workspace/alerts-list-states";
import type { AlertHistoryFilterState } from "@/alerts/types/alerts-workspace";
import { useAlertsBreadcrumbs } from "@/alerts/hooks/useAlertsBreadcrumbs";

/** The wire values, in the order an operator scans them. */
const STATUS_VALUES = [AlertStatus.Pending, AlertStatus.Resolved, AlertStatus.Ignored];

const AlertsHistoryPage = () => {
  const { t } = useTranslation("alerts");
  const organizationId = useOrganizationStore((s) => s.selectedOrganizationId);
  const { value: range } = useDateTimeRangeStore();
  const store = useAlertsWorkspaceStore();
  const locationsQuery = useGetAllLocations();
  const locations = useMemo(() => locationsQuery.data?.data ?? [], [locationsQuery.data]);

  // Label options come from the canonical classification taxonomy rather than a
  // list maintained here — a hand-kept copy drifts, and a filter offering a
  // label the backend no longer knows silently returns nothing.
  const taxonomyQuery = useGetClassificationTaxonomy();
  const labelOptions = useMemo(() => {
    const taxonomy = taxonomyQuery.data;
    if (!taxonomy) return [];
    const members = taxonomy.families.flatMap((family) =>
      family.members.map((member) => ({
        value: member.canonical,
        label: `${family.display_name} · ${member.display_name}`,
      })),
    );
    // Deprecated "other" labels stay selectable: historical alerts still carry
    // them, and a search that cannot reach them is not a complete history.
    const others = taxonomy.other_labels.map((other) => ({
      value: other.canonical,
      label: other.display_name,
    }));
    return [...members, ...others];
  }, [taxonomyQuery.data]);

  // Zustand actions are defined once, so these deps are stable.
  const { syncOrganization, resetHistoryPagination, setHistoryLocation } = store;

  useEffect(() => {
    syncOrganization(organizationId);
  }, [organizationId, syncOrganization]);

  // The range lives in the shared picker, so History has to react to it: moving
  // the window is a new search, and page 4 of the old one means nothing in it.
  const rangeKey = `${range.fromDate.getTime()}-${range.toDate.getTime()}`;
  useEffect(() => {
    resetHistoryPagination();
  }, [rangeKey, resetHistoryPagination]);

  // The API requires EXACTLY ONE scope — "all locations" is not a query it
  // supports, and sending none comes back 400. So History always carries a
  // location, healed to the first available one the same way the Queue does.
  useEffect(() => {
    if (locations.length === 0) return;
    if (!locations.some((l) => l.id === store.historyLocationId)) {
      setHistoryLocation(locations[0].id);
    }
  }, [locations, store.historyLocationId, setHistoryLocation]);

  const selectedLocation = locations.find((l) => l.id === store.historyLocationId) ?? null;
  const machines = selectedLocation?.machines ?? [];

  useAlertsBreadcrumbs({
    locationId: store.historyLocationId,
    onSelectLocation: setHistoryLocation,
  });

  const filterState: AlertHistoryFilterState | null =
    store.historyLocationId == null
      ? null
      : {
          // The builder resolves this to ONE effective scope (device → machine
          // → location); this page never decides the precedence itself.
          scope: {
            location_id: store.historyLocationId,
            machine_ids: store.historyMachineIds,
          },
          start_time: range.fromDate,
          end_time: range.toDate,
          status_in: store.historyStatuses,
          type_in: store.historyTypes,
          label_in: store.historyLabels,
          min_confidence: store.historyMinConfidence,
          max_confidence: store.historyMaxConfidence,
          ordering: store.historyOrdering,
          include_preview: true,
          limit: store.historyLimit,
          offset: store.historyOffset,
        };

  const query = useAlertHistory(filterState);
  const page = query.data;
  const rows = page?.items ?? [];

  const pageNumber = page?.page ?? 1;
  const pageCount = page?.pageCount ?? 0;

  // `value` stays the raw status the API filters on; only the label is translated.
  // Three entries, rebuilt each render so a language change is picked up.
  const statusOptions = STATUS_VALUES.map((status) => ({
    value: status as string,
    label: alertStatusLabel(status),
  }));

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
      <ScopeSelect
        label={t("filters.labels.location")}
        placeholder={t("filters.labels.locationPlaceholder")}
        value={store.historyLocationId}
        options={locations.map((location) => ({ value: location.id, label: location.name }))}
        onChange={store.setHistoryLocation}
      />
      <MultiSelect
        label={t("filters.labels.machines")}
        values={store.historyMachineIds}
        options={machines.map((machine) => ({ value: machine.id, label: machine.name }))}
        onChange={store.setHistoryMachines}
        disabled={store.historyLocationId == null}
      />
      <MultiSelect
        label={t("filters.labels.statuses")}
        values={store.historyStatuses as string[]}
        options={statusOptions}
        onChange={(values) => store.setHistoryStatuses(values as AlertStatus[])}
      />
      <MultiSelect
        label={t("filters.labels.types")}
        values={store.historyTypes}
        options={ALERT_TYPES.map((type) => ({ value: type as string, label: alertTypeLabel(type) }))}
        onChange={store.setHistoryTypes}
      />
      <MultiSelect
        label={t("filters.labels.classifications")}
        values={store.historyLabels}
        options={labelOptions}
        onChange={store.setHistoryLabels}
        disabled={labelOptions.length === 0}
      />
      <ConfidenceRange
        min={store.historyMinConfidence}
        max={store.historyMaxConfidence}
        onChange={store.setHistoryConfidence}
      />
      <OrderingToggle value={store.historyOrdering} onChange={store.setHistoryOrdering} />
      <div className="ml-auto flex items-center gap-3">
        <RefreshingChip show={query.isFetching && !query.isPending} />
        <RefreshButton isFetching={query.isFetching} onRefresh={() => void query.refetch()} />
        <PageSizeSelect
          value={store.historyLimit}
          options={ALERT_PAGE_SIZES}
          onChange={store.setHistoryLimit}
        />
      </div>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
      <span className="tabular-nums">
        {/* Straight from metadata — never counted off the loaded rows. */}
        {page
          ? t("history.footer.range", {
              from: page.totalItems === 0 ? 0 : store.historyOffset + 1,
              to: Math.min(store.historyOffset + rows.length, page.totalItems),
              total: page.totalItems,
              page: pageNumber,
              pages: Math.max(pageCount, 1),
            })
          : "—"}
      </span>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          disabled={store.historyOffset === 0 || query.isFetching}
          onClick={() => store.setHistoryOffset(store.historyOffset - store.historyLimit)}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {t("pagination.previous")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          disabled={pageNumber >= pageCount || query.isFetching}
          onClick={() => store.setHistoryOffset(store.historyOffset + store.historyLimit)}
        >
          {t("pagination.next")}
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );

  return (
    <AlertsScopeGate
      organizationId={organizationId}
      locations={locations}
      isLoading={locationsQuery.isPending}
      isError={locationsQuery.isError}
    >
      <AlertMasterDetail
        mode="history"
        rows={rows}
        selectedId={store.selectedHistoryAlertId}
        onSelect={store.selectHistoryAlert}
        toolbar={toolbar}
        banner={<TruncatedNotice show={page?.truncated ?? false} />}
        footer={footer}
        emptyState={<HistoryEmptyState />}
        isInitialLoading={query.isPending && filterState != null}
        error={query.error}
        onRetry={() => void query.refetch()}
        renderDetail={({ row, onClose, onNext }) => (
          <AlertDetailPane row={row} onClose={onClose} onNext={onNext} />
        )}
      />
    </AlertsScopeGate>
  );
};

export default AlertsHistoryPage;
